import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectIsAuthenticated } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import SearchHeader from './components/SearchHeader';
import FilterChips from './components/FilterChips';
import PropertyGrid from './components/PropertyGrid';
import MapView from './components/MapView';
import HeroSlider from './components/HeroSlider';
import CategoriesSlider from './components/CategoriesSlider';
import { useGetSliderQuery, useGetCategoriesQuery, useGetUserFavouritePropertyQuery } from '../../features/api/apiSlice';
import usePropertySearch from '../../lib/hooks/usePropertySearch'; // Import usePropertySearch hook
import SEO from '../../components/seo'; // Import the SEO component

const initialFilters = {
  location: '',
  transactionType: 'all',
  propertyType: 'all',
  priceRange: [0, 500000000], // Max price for Uganda
  status: 'all',
  sortBy: 'relevance',
  bedrooms: 'any',
  bathrooms: 'any',
  sqftRange: [0, 10000], // Max sqft
  yearBuiltRange: [1900, new Date().getFullYear()],
  amenities: [],
  features: [],
  recentlyAdded: 'any',
  hasVirtualTour: false,
  distanceFromCity: 50, // Default max distance
  hoaFeeMax: '',
  lotSizeMin: ''
};

const PropertySearchBrowse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const user = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data: userFavoriteProperties = [] } = useGetUserFavouritePropertyQuery(undefined, {
    skip: !isAuthenticated,
  });

  const favoritePropertyIds = userFavoriteProperties.map(fav => fav.property_id);

  const [selectedProperty, setSelectedProperty] = useState(null);
  
  const [viewMode, setViewMode] = useState('grid');

  const {
    properties,
    isLoading,
    isFetching,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    totalPages,
    totalResults,
    updateUrlParams,
    handleFiltersChange,
    handleFilterRemove,
    handleClearFilters,
    handleSearchChange,
    handlePageChange,
    page, // Destructure 'page' from the hook
  } = usePropertySearch(initialFilters, 23); // Use the custom hook with 23 items per page

  const { data: sliderSlides = [] } = useGetSliderQuery();
  const { data: categories = [] } = useGetCategoriesQuery();

  // Initialize viewMode from URL params
  useEffect(() => {
    setViewMode(searchParams.get('view') || 'grid');
  }, [searchParams]);

  // Update URL params for viewMode
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    updateUrlParams(filters, searchQuery, filters.page, mode); // Pass current page and new view mode
  };

  const handleToggleFavorite = (propertyId) => {
    console.log(`Toggle favorite for property ${propertyId}`);
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
  };

  const selectedCategory = categories.find(cat => String(cat.id) === String(filters?.propertyType));
  const searchContext = {
    location: filters?.location,
    propertyType: selectedCategory?.category || filters?.propertyType,
    priceRange: filters?.priceMin || filters?.priceMax ? 
      `${filters?.priceMin ? '$' + parseInt(filters?.priceMin)?.toLocaleString() : ''}${filters?.priceMin && filters?.priceMax ? ' - ' : ''}${filters?.priceMax ? '$' + parseInt(filters?.priceMax)?.toLocaleString() : ''}` : null,
    queryString: '' // The URL params are now managed by the hook
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Find Properties"
        description="Search for properties, houses, apartments, hostels, and hotels for sale or rent in Uganda and across Africa. Advanced search filters available."
        keywords="property search uganda, real estate listings africa, homes for sale, apartments for rent, commercial property, hostels, hotels, advanced property search"
        canonicalUrl="https://hirebroker.co/property-search-browse"
      />
      <Header />
      <main className="pt-16">
        

        <SearchHeader
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          resultsCount={totalResults}
          filters={filters}
          categories={categories}
          onFiltersChange={handleFiltersChange}
          onSearch={() => { /* Handled by debounce */ }}
          isLoading={isLoading || isFetching}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumbs */}
          <Breadcrumbs searchContext={searchContext} className="mb-6" />

          

          {/* Filter Chips */}
          <FilterChips
            filters={filters}
            categories={categories}
            onRemoveFilter={handleFilterRemove}
            onClearAll={handleClearFilters}
          />

          <div className="flex gap-6">
            <div className="flex-1 min-w-0">
              {viewMode === 'grid' ? (
                <PropertyGrid
                  properties={properties}
                  loading={isLoading || isFetching}
                  favoritePropertyIds={favoritePropertyIds}
                  currentPage={page}
                  totalPages={totalPages}
                  totalResults={totalResults}
                  onPageChange={handlePageChange}
                />
              ) : (
                <div className="h-[600px] lg:h-[700px]">
                  <MapView
                    properties={properties}
                    onPropertySelect={handlePropertySelect}
                    selectedProperty={selectedProperty}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PropertySearchBrowse;
